import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  FormControl,
  FormLabel,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import AnimatedToggleButtonGroup from "../Animated/AnimatedToggleButtonGroup";
import DownloadIcon from "@mui/icons-material/Download";
import html2pdf from "html2pdf.js";
import HeightChart from "./HeightChart";
import WeightChart from "./WeightChart";
import BMIChart from "./BMIChart";
import { LoadingButton } from "@mui/lab";
import { useNavigate } from "react-router-dom";
import { FormProvider, useForm } from "react-hook-form";
import SkeletonWrapper from "../Skeleton/SkeletonWrapper";
import { CustomTextField } from "../CustomFields/TextField";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import { handleFocus, useQuery } from "../../shared/CommonFunction";
import { EncryptDecryptService } from "../../services/common/EncryptionService";
import { PreferencesContext } from "../../PreferenceContext";
import {
  CreatePatient,
  GetPatientDetail,
  UpdatePatient,
  UpdatePatientOPDAppointment,
} from "../../services/PatientService";
import { AlertType } from "../../services/models/AlertType";
import { createInputPropsForNumberField } from "../../shared/data";
import GrowthChartItemList from "../../pages/GrowthChartItem/GrowthChartItemList";
import { format, isValid } from "date-fns";
import {
  GetGrowthChart,
  GetMemberPhysicalGrowthList,
} from "../../services/models/GrowthChart";
import PrintIcon from "@mui/icons-material/Print";
import LocationOnRoundedIcon from "@mui/icons-material/LocationOnRounded";
import HeadCircumferenceChart from "./HeadCircumferenceChart";
import CustomTooltip from "../ToolTip/CustomToolTip";

// PAGE SIZE
const PAGE_SIZE = 100;

interface formInitialValueTypes {
  patientId: number;
  opdId: number;
  birthWeight: number | null;
  motherHeight: number | null;
  fatherHeight: number | null;
  midParentalHeight: number | null;
  isDeleted: boolean;
}

interface Parameter {
  memberPhysicalGrowthId: string;
  patientId: string;
  opdId: string;
  pageIndex: number;
  pageSize: number;
  sortColumn: string;
  sortDirection: string;
  searchValue: string;
  tabType: number;
  startDate: Date;
  endDate: Date;
}

const parameters: Parameter = {
  memberPhysicalGrowthId: "",
  patientId: "",
  opdId: "",
  pageIndex: 1,
  pageSize: PAGE_SIZE,
  sortColumn: "",
  sortDirection: "",
  searchValue: "",
  tabType: 0,
  startDate: new Date(),
  endDate: new Date(),
};

// SCHEMA FOR RESULTDATA
interface ResultData {
  memberPhysicalGrowthColl: Array<any>;
  tabTypeColl: Array<any>;
  totalCount: number;
}

// DEFAULT VALUES FOR RESULT DATA
const initResultData: ResultData = {
  memberPhysicalGrowthColl: [],
  tabTypeColl: [],
  totalCount: 0,
};

interface GrowthChartData {
  growthChartSeriesColl: Array<any>;
  heightChartZoneColl: Array<any>;
  weightChartZoneColl: Array<any>;
  bmiChartZoneColl: Array<any>;
  hcChartZoneColl: Array<any>;
}

const initHeightData: GrowthChartData = {
  growthChartSeriesColl: [],
  heightChartZoneColl: [],
  weightChartZoneColl: [],
  bmiChartZoneColl: [],
  hcChartZoneColl: [],
};

const GrowthChart = (Props: any) => {
  // Props
  const {
    opd,
    isForPopUp,
    handleClosePopUp,
    code,
    popUpReason,
    memberPhysicalGrowthId,
  } = Props;

  const isMobileScreen = useMediaQuery("(max-width:600px)");

  const growthChartRef = React.useRef<{
    triggerChildFunction: () => void;
  } | null>(null);

  const [view, setView] = useState<"chart" | "details">("chart");
  const [patientData, setPatientData] = useState<any>(null);
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const edService = new EncryptDecryptService();
  const { userContext, updateUserContext } = useContext(PreferencesContext);
  const heightProps = createInputPropsForNumberField(3, 1, true);
  const htmlprintRef = useRef<HTMLTableElement>(null);
  const [param, setParam] = useState(parameters);
  const [counData, setCounData] = useState(0);
  const [resultData, setResultData] = useState<ResultData>(initResultData);
  const [pageStartIndex, setPageStartIndex] = useState(1);
  const [pageEndIndex, setPageEndIndex] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [page_Index, setPage_Index] = useState(1);

  const [ageCountData, setAgeCountData] = useState(-1);
  const [growthChartData, setGrowthChartData] = useState(initHeightData);

  const [isDownload, setIsDownload] = useState(false);

  const graphTab = [
    { label: "CHART", value: "chart" },
    { label: "DETAILS", value: "details" },
  ];

  const initPatientData: formInitialValueTypes = {
    patientId: opd?.patientId || 0,
    opdId: opd?.opdId || 0,
    birthWeight: null,
    motherHeight: null,
    fatherHeight: null,
    midParentalHeight: null,
    isDeleted: false,
  };

  const validationSchema = Yup.object().shape({
    patientId: Yup.number().nullable(),
    birthWeight: Yup.number()
      .max(999, "Max weight limit is 999")
      .nullable()
      .transform((value, originalValue) =>
        originalValue === "" ? null : value,
      ),
    motherHeight: Yup.number()
      .max(999, "Max MotherHeight limit is 999")
      .nullable()
      .transform((value, originalValue) =>
        originalValue === "" ? null : value,
      ),
    fatherHeight: Yup.number()
      .max(999, "Max FatherHeight limit is 999")
      .nullable()
      .transform((value, originalValue) =>
        originalValue === "" ? null : value,
      ),
    weight: Yup.number()
      .max(999, "Max weight limit is 999")
      .nullable()
      .transform((value, originalValue) =>
        originalValue === "" ? null : value,
      ),
    height: Yup.number()
      .max(999, "Max height limit is 999")
      .nullable()
      .transform((value, originalValue) =>
        originalValue === "" ? null : value,
      ),
    expectedHeight: Yup.number()
      .max(999, "Max Expected Height limit is 999")
      .nullable()
      .transform((value, originalValue) =>
        originalValue === "" ? null : value,
      ),
    isDeleted: Yup.boolean(),
  });

  // GETTTING PARAMS
  let urlParams = useQuery();
  let curRawPatientId = urlParams.get("q");
  let curPatientId = 0;

  if (!isForPopUp) {
    if (curRawPatientId !== null && curRawPatientId !== undefined) {
      let decId = edService.decrypt(curRawPatientId);
      if (decId !== "" && decId !== null && decId !== undefined) {
        curPatientId = +decId;
      }
    }
  } else {
    if (isForPopUp && opd?.patientId > 0) {
      curPatientId = opd.patientId;
    }
  }

  // FORM HOOK
  const methods = useForm({
    resolver: yupResolver(validationSchema), // IGNORE THIS ERROR
    defaultValues: initPatientData, // DEFAULT VALUE -- DON'T CREATE STATE FOR IT -- STATE CAN INCREASE SITE LOAD
  });

  // CALCULATE THE MID-PARENTAL HEIGHT
  const gender = opd?.patientGender?.toLowerCase();
  const motherHeight = methods.watch("motherHeight");
  const fatherHeight = methods.watch("fatherHeight");
  const weightProps = createInputPropsForNumberField(3, 3, true);

  const midParentalHeight = useMemo(() => {
    const m =
      typeof motherHeight === "number"
        ? motherHeight
        : Number(motherHeight || "0");
    const f =
      typeof fatherHeight === "number"
        ? fatherHeight
        : Number(fatherHeight || "0");

    if ((isNaN(m) && isNaN(f)) || (m === 0 && f === 0)) return "0.00";

    const adjustment = gender?.toLowerCase() === "male" ? 13 : -13;
    return ((m + f + adjustment) / 2).toFixed(2);
  }, [motherHeight, fatherHeight, gender]);

  const handleChartDownload = async () => {
    setIsDownload(true);
    await new Promise((r) => setTimeout(r, 1500));
    const element = htmlprintRef.current;
    if (!element) return;

    let documentName = opd?.patientNumber?.toString() || "document";
    documentName = documentName.replace(/\//g, "_");

    const patientName = opd?.patientName || "undefined";

    const contentHeight = element.offsetHeight;
    const contentWidth = element.offsetWidth;

    html2pdf()
      .set({
        margin: [10, 10, 10, 10],
        image: { type: "jpeg", quality: 1 },
        html2canvas: {
          scale: 2,
          scrollY: 0,
          width: contentWidth,
          minHeight: contentHeight,
        },
        filename: `#${documentName}_${patientName} - Growth Chart.pdf`,
        jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
        pagebreak: {
          mode: ["css", "legacy"],
        },
      })
      .from(element)
      .save()
      .finally(() => setIsDownload(false));
  };

  const getPatientDetailById = async () => {
    let Patient: any;
    let params = new URLSearchParams();
    params.append("patientId", curPatientId?.toString());
    params.append("opdId", opd?.opdId?.toString());
    params.append("branchId", userContext?.branchId?.toString());
    params.append("orgId", userContext?.organizationId?.toString());
    params.append("consumerId", userContext?.consumerId?.toString());
    try {
      const res: any = await GetPatientDetail(params);
      if (res?.data?.data) {
        Patient = res.data.data;
        setPatientData(Patient);
        methods.reset({
          ...initPatientData,
          patientId: Patient?.patientId || 0,
          birthWeight: Patient?.birthWeight || 0,
          motherHeight: Patient?.motherHeight,
          fatherHeight: Patient?.fatherHeight,
        });
      }

      return Patient;
    } catch (e) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage:
          typeof e === "string" ? (e as string) : (e?.message as string),
        alertType: AlertType.Error,
      });
    }
  };

  // SAVE AND UPDATE
  const savePatient = async (value: formInitialValueTypes) => {
    try {
      let result: any = "";
      let formData = {
        ...patientData,
        ...value,
        opdId: opd?.opdId || 0,
        appointmentId: opd?.appointmentId || 0,
        branchId: userContext?.branchId,
        orgId: userContext?.organizationId,
        consumerId: userContext?.consumerId,
        DataManagedBy: userContext.loginDetail.userId,
      };
      if (curPatientId > 0) {
        if (popUpReason === "OPD_PATIENT_UPDATE") {
          result = await UpdatePatientOPDAppointment(formData);
        } else {
          result = await UpdatePatient(formData);
        }
      }
      if (result?.data) {
        await getGrowthList();
        if (growthChartRef.current) {
          await growthChartRef.current.triggerChildFunction(); // Call child's function
        }
        if (!isForPopUp) {
          navigate(-1);
        } else {
          if (result?.data) {
            if (code === "PATIENT") {
              handleClosePopUp("PATIENT_CREATE", result?.data);
            } else if (code === "REFERRAL_PATIENT") {
              handleClosePopUp("REFERRAL_PATIENT_CREATE", result?.data);
            }
          }
        }
        updateUserContext({
          ...userContext,
          isAlert: true,
          alertMessage: result?.message,
          alertType: AlertType.Success,
        });
      } else {
        updateUserContext({
          ...userContext,
          isAlert: true,
          alertMessage: "Something went wrong.",
          alertType: AlertType.Error,
        });
      }
    } catch (e) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage:
          typeof e === "string" ? (e as string) : (e?.message as string),
        alertType: AlertType.Error,
      });
    }
  };

  const getGrowthList = async () => {
    try {
      let params = new URLSearchParams();
      params.append("patientId", opd.patientId?.toString());
      params.append("pageIndex", param.pageIndex?.toString());
      params.append("pageSize", param.pageSize?.toString());
      params.append("sortColumn", param.sortColumn);
      params.append("sortDirection", param.sortDirection);

      if (param?.startDate) {
        params.append("startDate", format(param.startDate, "yyyy-MM-dd"));
      }
      if (param?.endDate) {
        params.append("endDate", format(param.endDate, "yyyy-MM-dd"));
      }

      const result: any = await GetMemberPhysicalGrowthList(params);
      if (result?.data?.data?.memberPhysicalGrowthColl) {
        const resultData: ResultData = result?.data?.data;
        let memberPhysicalGrowthColl =
          resultData?.memberPhysicalGrowthColl || [];
        let dataCount = resultData?.totalCount || 0;
        let showDataCount = Math.ceil(dataCount / param.pageSize);
        setCounData(showDataCount);
        setResultData(resultData);
        let startIndex = (page_Index - 1) * param.pageSize + 1;
        let endIndex =
          (page_Index - 1) * param.pageSize +
          (memberPhysicalGrowthColl?.length || 0);
        setPageStartIndex(startIndex);
        setPageEndIndex(endIndex);
        setTotalCount(dataCount);
      } else {
      }
      setIsLoading(false);
    } catch (e) {
      setIsLoading(false);
      console.log("err:", e);
    }
  };

  const getMemberPhysicalGrowthChart = async () => {
    try {
      let params = new URLSearchParams();

      params.append("patientId", opd?.patientId?.toString());
      params.append(
        "startDate",
        format(new Date(opd?.patientBirthDate || ""), "yyyy-MM-dd"),
      );
      if (param?.endDate) {
        params.append("endDate", format(param.endDate, "yyyy-MM-dd"));
      }

      const result: any = await GetGrowthChart(params);

      if (result?.data?.data?.growthChartSeriesColl) {
        const growthChartData: GrowthChartData = result?.data?.data;
        setGrowthChartData(growthChartData);
      }

      if (userContext?.memberDetail?.age === 0) {
        setAgeCountData(0);
      }
    } catch (e) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage:
          typeof e === "string" ? (e as string) : (e?.message as string),
        alertType: AlertType.Error,
      });
    }
    setIsLoading(false);
  };

  // FETCH DATA
  const fetchData = async () => {
    try {
      setIsLoading(true);
      if (curPatientId > 0) {
        await getPatientDetailById();
        await getGrowthList();
        await getMemberPhysicalGrowthChart();
      }
      setIsLoading(false);
    } catch (e) {
      setIsLoading(false);
      console.log(e);
    }
  };

  const handleBack = () => {
    if (isForPopUp) {
      handleClosePopUp("CLOSE");
    } else {
      navigate(-1);
    }
  };

  const onSubmit = async (data: any) => {
    setIsLoading(true);
    await savePatient(data);
    setIsLoading(false);
  };

  useEffect(() => {
    setIsLoading(true);
    fetchData();
  }, [curPatientId]);

  return (
    <>
      <Box className={"main-container"}>
        <Grid container>
          <Grid
            item
            xs={12}
            sx={{
              position: "sticky",
              top: 0,
              background: "#fff",
              width: "100%",
              zIndex: 999,
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                width: "100%",
                margin: "14px 0px 14px 0px",
              }}
            >
              <Box sx={{ display: "flex", gap: 1 }}>
                <AnimatedToggleButtonGroup
                  value={view}
                  onChange={(val) => {
                    setView(val as "chart" | "details");
                    getGrowthList();
                    if (val === "chart") {
                      getMemberPhysicalGrowthChart();
                    }
                  }}
                  options={graphTab}
                  fullWidth={false}
                  sx={{
                    minWidth: 240,
                    fontSize: "8px",
                    "& .MuiToggleButton-root, & .MuiButtonBase-root": {
                      height: "36px",
                    },
                    "& .MuiToggleButton-root.Mui-selected": {
                      backgroundColor: "#285cdb !important",
                      color: "#fff",
                    },
                  }}
                />
              </Box>
              <Box
                display="flex"
                justifyContent="flex-start"
                alignItems="center"
              >
                <Tooltip title="Download Chart" placement="top">
                  <IconButton
                    aria-label="Download Chart"
                    onClick={handleChartDownload}
                  >
                    <DownloadIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          </Grid>

          {view === "chart" &&
            (() => {
              const growthSeries = growthChartData?.growthChartSeriesColl ?? [];

              const hasHeight = growthSeries.some((item) => item?.height > 0);
              const hasWeight = growthSeries.some((item) => item?.weight > 0);
              const hasBMI = growthSeries.some((item) => item?.bmi > 0);
              const hasHead = growthSeries.some(
                (item) => item?.headCircumference > 0,
              );

              const hasAnyChart = hasHeight || hasWeight || hasBMI || hasHead;

              if (!hasAnyChart) {
                return (
                  <div
                    className="summary-text"
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      height: "100px",
                      width: "100%",
                    }}
                  >
                    No chart data found
                  </div>
                );
              }

              return (
                <>
                  <table
                    style={{
                      width: "100%",
                      marginTop: "16px",
                      marginBottom: "20px",
                      textAlign: "center",
                    }}
                  >
                    <tbody>
                      {/* Height Chart */}
                      {hasHeight && (
                        <>
                          <tr>
                            <td>
                              <HeightChart
                                motherHeight={
                                  growthChartData?.growthChartSeriesColl?.[0]
                                    ?.motherHeight ?? 0
                                }
                                fatherHeight={
                                  growthChartData?.growthChartSeriesColl?.[0]
                                    ?.fatherHeight ?? 0
                                }
                                heightList={
                                  growthChartData?.growthChartSeriesColl
                                }
                                intervalType={
                                  growthChartData?.growthChartSeriesColl?.[0]
                                    ?.intervalType ?? "YEAR"
                                }
                                heightChartZoneData={
                                  growthChartData?.heightChartZoneColl
                                }
                                isMobileScreen={isMobileScreen}
                              />
                            </td>
                          </tr>

                          <tr>
                            <td style={{ height: "40px" }}></td>
                          </tr>
                        </>
                      )}

                      {/* Weight Chart */}
                      {hasWeight && (
                        <>
                          <tr>
                            <td>
                              <WeightChart
                                weightList={
                                  growthChartData?.growthChartSeriesColl
                                }
                                intervalType={
                                  growthChartData?.growthChartSeriesColl?.[0]
                                    ?.intervalType ?? "YEAR"
                                }
                                weightChartZoneData={
                                  growthChartData?.weightChartZoneColl
                                }
                                isMobileScreen={isMobileScreen}
                              />
                            </td>
                          </tr>

                          <tr>
                            <td style={{ height: "40px" }}></td>
                          </tr>
                        </>
                      )}

                      {/* BMI Chart */}
                      {hasBMI && (
                        <>
                          <tr>
                            <td>
                              <BMIChart
                                bmiList={growthChartData?.growthChartSeriesColl}
                                intervalType={
                                  growthChartData?.growthChartSeriesColl?.[0]
                                    ?.intervalType ?? "YEAR"
                                }
                                bmiChartZoneData={
                                  growthChartData?.bmiChartZoneColl
                                }
                                isMobileScreen={isMobileScreen}
                              />
                            </td>
                          </tr>

                          <tr>
                            <td style={{ height: "40px" }}></td>
                          </tr>
                        </>
                      )}

                      {/* Head Circumference Chart */}
                      {hasHead && (
                        <tr>
                          <td>
                            <HeadCircumferenceChart
                              headCircumferenceList={
                                growthChartData?.growthChartSeriesColl
                              }
                              intervalType={
                                growthChartData?.growthChartSeriesColl?.[0]
                                  ?.intervalType ?? "YEAR"
                              }
                              headCircumferenceChartZoneData={
                                growthChartData?.hcChartZoneColl
                              }
                              isMobileScreen={isMobileScreen}
                            />
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </>
              );
            })()}

          {view === "details" && (
            <>
              <Box className={"main-container"} mt={2}>
                <FormProvider {...methods}>
                  <form
                    onSubmit={methods.handleSubmit(onSubmit)}
                    action=""
                    autoComplete="off"
                  >
                    <Grid container spacing={2}>
                      {/* BIRTH WEIGHT */}
                      <Grid item xs={12} sm={4} md={2.5}>
                        <FormControl fullWidth>
                          <FormLabel
                            className={`fileds-label-text  ${
                              methods.formState.errors.birthWeight
                                ? "validation-message"
                                : ""
                            }`}
                          >
                            Birth Weight{" "}
                            <CustomTooltip
                              title="This field is important for automated dose calculation"
                              hideHeader={true}
                            ></CustomTooltip>
                          </FormLabel>
                          <SkeletonWrapper
                            isLoading={isLoading}
                            className="skeleton-input"
                          >
                            <CustomTextField
                              placeholder="000.000"
                              fullWidth
                              id="birthWeight"
                              size="small"
                              disabled={false}
                              inputProps={{
                                ...weightProps,
                              }}
                              {...methods.register("birthWeight")}
                              onFocus={handleFocus}
                              InputProps={{
                                endAdornment: (
                                  <InputAdornment position="end">
                                    Kg
                                  </InputAdornment>
                                ),
                              }}
                              error={!!methods.formState.errors.birthWeight}
                            />
                          </SkeletonWrapper>
                          <Typography className="validation-message">
                            {methods.formState.errors.birthWeight?.message}
                          </Typography>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} sm={4} md={2.5}>
                        <FormControl fullWidth>
                          <FormLabel
                            className={`fileds-label-text  ${
                              methods.formState.errors.motherHeight
                                ? "validation-message"
                                : ""
                            }`}
                          >
                            Mother's Height{" "}
                          </FormLabel>
                          <SkeletonWrapper
                            isLoading={isLoading}
                            className="skeleton-input"
                          >
                            <CustomTextField
                              placeholder="000.0"
                              fullWidth
                              id="motherHeight"
                              size="small"
                              inputProps={{
                                ...heightProps,
                              }}
                              {...methods.register("motherHeight")}
                              value={methods.watch("motherHeight") || ""}
                              onFocus={handleFocus}
                              InputProps={{
                                endAdornment: (
                                  <InputAdornment position="end">
                                    cm
                                  </InputAdornment>
                                ),
                              }}
                              error={!!methods.formState.errors.motherHeight}
                            />
                          </SkeletonWrapper>
                          <Typography className="validation-message">
                            {methods.formState.errors.motherHeight?.message}
                          </Typography>
                        </FormControl>
                      </Grid>

                      <Grid item xs={12} sm={4} md={2.5}>
                        <FormControl fullWidth>
                          <FormLabel
                            className={`fileds-label-text  ${
                              methods.formState.errors.fatherHeight
                                ? "validation-message"
                                : ""
                            }`}
                          >
                            Father's Height{" "}
                          </FormLabel>
                          <SkeletonWrapper
                            isLoading={isLoading}
                            className="skeleton-input"
                          >
                            <CustomTextField
                              placeholder="000.0"
                              fullWidth
                              id="fatherHeight"
                              size="small"
                              inputProps={{
                                ...heightProps,
                              }}
                              {...methods.register("fatherHeight")}
                              value={methods.watch("fatherHeight") || ""}
                              onFocus={handleFocus}
                              InputProps={{
                                endAdornment: (
                                  <InputAdornment position="end">
                                    cm
                                  </InputAdornment>
                                ),
                              }}
                              error={!!methods.formState.errors.fatherHeight}
                            />
                          </SkeletonWrapper>
                          <Typography className="validation-message">
                            {methods.formState.errors.fatherHeight?.message}
                          </Typography>
                        </FormControl>
                      </Grid>

                      <Grid item xs={12} sm={4} md={2}>
                        <FormControl fullWidth>
                          <FormLabel
                            className="fileds-label-text"
                            sx={{ textAlign: "right" }}
                          >
                            Mid Parental Height
                          </FormLabel>
                          <SkeletonWrapper
                            isLoading={isLoading}
                            className="skeleton-input"
                          >
                            <Typography
                              sx={{
                                textAlign: "right",
                                marginTop: "10px",
                              }}
                            >
                              {midParentalHeight && midParentalHeight !== "0.00"
                                ? `${midParentalHeight} cm`
                                : "0.00 cm"}
                            </Typography>
                          </SkeletonWrapper>
                        </FormControl>
                      </Grid>

                      <Grid item xs={12} sm={6} md={2.5} mt={3.2}>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "end",
                            gap: 2,
                          }}
                        >
                          {opd?.isEditable && (
                            <LoadingButton
                              color="primary"
                              loadingPosition="start"
                              startIcon={<></>}
                              variant="contained"
                              type="submit"
                              className="primary-button"
                              name="save"
                              disabled={isLoading}
                            >
                              Save
                            </LoadingButton>
                          )}
                          <LoadingButton
                            color="primary"
                            loadingPosition="start"
                            startIcon={<></>}
                            variant="outlined"
                            type="button"
                            className="secondary-button"
                            name="cancel"
                            onClick={handleBack}
                          >
                            Close
                          </LoadingButton>
                        </Box>
                      </Grid>
                    </Grid>
                  </form>
                </FormProvider>
              </Box>

              <Box className={"main-container"} mt={5}>
                <GrowthChartItemList
                  opd={opd}
                  isForPopUp={isForPopUp}
                  ref={growthChartRef}
                />
              </Box>
            </>
          )}
        </Grid>

        {/* HIDDEN PDF CONTENT */}
        {isDownload &&
          (() => {
            const growthSeries = growthChartData?.growthChartSeriesColl ?? [];

            const hasHeight = growthSeries.some((item) => item?.height > 0);
            const hasWeight = growthSeries.some((item) => item?.weight > 0);
            const hasBMI = growthSeries.some((item) => item?.bmi > 0);
            const hasHead = growthSeries.some(
              (item) => item?.headCircumference > 0,
            );

            const hasAnyChart = hasHeight || hasWeight || hasBMI || hasHead;

            if (!hasAnyChart) {
              return null; // hide everything if no chart data
            }

            return (
              <div ref={htmlprintRef} style={{ width: "1200px" }}>
                <table style={{ width: "100%", marginTop: "16px" }}>
                  {/* Header */}
                  <tr>
                    <td>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "flex-start",
                          gap: 1,
                          height: "100%",
                          marginBottom: "30px",
                          flexWrap: "wrap",
                          paddingLeft: "60px",
                        }}
                      >
                        <span
                          style={{
                            fontSize: "26px",
                            fontWeight: "bold",
                            textAlign: "left",
                          }}
                        >
                          Growth Chart
                        </span>
                        <Box
                          sx={{
                            fontWeight: "bold",
                          }}
                        >
                          <span style={{ fontSize: "22px" }}>
                            {`${
                              opd?.patientNumber
                                ? "#" + opd?.patientNumber + " - "
                                : ""
                            }`}{" "}
                            {opd?.patientName}{" "}
                          </span>
                          <span className="text-saperator">|</span>
                          <span
                            style={{
                              fontSize: 22,
                              fontWeight: "bold",
                              color:
                                opd?.patientGender === "MALE"
                                  ? " #2e67a3"
                                  : opd?.patientGender === "FEMALE"
                                    ? "#BC3965"
                                    : "black",
                            }}
                          >
                            {` ${
                              opd?.patientGender === "MALE"
                                ? "M"
                                : opd?.patientGender === "FEMALE"
                                  ? "F"
                                  : "O"
                            } `}
                          </span>
                          <span
                            style={{
                              fontSize: 22,
                              fontWeight: "bold",
                              color: "#414D56",
                            }}
                          >
                            {opd?.ageYear > 0 ? ` ${opd?.ageYear}y` : ""}
                            {opd?.ageMonth > 0 ? ` ${opd?.ageMonth}m` : ""}
                            {opd?.ageDay > 0 ? ` ${opd?.ageDay}d` : ""}
                          </span>
                          <span style={{ fontSize: "22px" }}>
                            {" ("}
                            {isValid(new Date(opd?.patientBirthDate))
                              ? format(
                                  new Date(opd?.patientBirthDate),
                                  "dd-MM-yyyy",
                                )
                              : ""}
                            {") "}
                          </span>
                          {(opd?.patientArea || opd?.patientCity) && (
                            <>
                              <span className="text-saperator">|</span>
                              <span
                                style={{ paddingLeft: "4px", fontSize: "20px" }}
                              >
                                <LocationOnRoundedIcon
                                  sx={{
                                    fontSize: 30,
                                    marginTop: "3px",
                                  }}
                                />
                                {`${opd?.patientArea}`}
                                {`${opd?.patientCity}`}
                              </span>
                            </>
                          )}
                        </Box>
                      </Box>
                    </td>
                  </tr>

                  {/* Height Chart */}
                  {hasHeight && (
                    <>
                      <tr>
                        <td>
                          <HeightChart
                            motherHeight={
                              growthChartData?.growthChartSeriesColl?.[0]
                                ?.motherHeight ?? 0
                            }
                            fatherHeight={
                              growthChartData?.growthChartSeriesColl?.[0]
                                ?.fatherHeight ?? 0
                            }
                            heightList={growthChartData?.growthChartSeriesColl}
                            intervalType={
                              growthChartData?.growthChartSeriesColl?.[0]
                                ?.intervalType ?? "YEAR"
                            }
                            heightChartZoneData={
                              growthChartData?.heightChartZoneColl
                            }
                            isMobileScreen={false}
                            isDownload={isDownload}
                          />
                        </td>
                      </tr>

                      <tr>
                        <td style={{ height: "40px" }}></td>
                      </tr>
                    </>
                  )}

                  {/* Weight Chart */}
                  {hasWeight && (
                    <>
                      <tr>
                        <td>
                          <WeightChart
                            weightList={growthChartData?.growthChartSeriesColl}
                            intervalType={
                              growthChartData?.growthChartSeriesColl?.[0]
                                ?.intervalType ?? "YEAR"
                            }
                            weightChartZoneData={
                              growthChartData?.weightChartZoneColl
                            }
                            isMobileScreen={false}
                            isDownload={isDownload}
                          />
                        </td>
                      </tr>

                      <tr>
                        <td style={{ height: "40px" }}></td>
                      </tr>
                    </>
                  )}

                  {/* BMI Chart */}
                  {hasBMI && (
                    <>
                      <tr>
                        <td>
                          <div style={{ marginTop: "70px" }}>
                            <BMIChart
                              bmiList={growthChartData?.growthChartSeriesColl}
                              intervalType={
                                growthChartData?.growthChartSeriesColl?.[0]
                                  ?.intervalType ?? "YEAR"
                              }
                              bmiChartZoneData={
                                growthChartData?.bmiChartZoneColl
                              }
                              isMobileScreen={false}
                              isDownload={isDownload}
                            />
                          </div>
                        </td>
                      </tr>

                      <tr>
                        <td style={{ height: "40px" }}></td>
                      </tr>
                    </>
                  )}

                  {/* Head Circumference Chart */}
                  {hasHead && (
                    <tr>
                      <td>
                        <HeadCircumferenceChart
                          headCircumferenceList={
                            growthChartData?.growthChartSeriesColl
                          }
                          intervalType={
                            growthChartData?.growthChartSeriesColl?.[0]
                              ?.intervalType ?? "YEAR"
                          }
                          headCircumferenceChartZoneData={
                            growthChartData?.hcChartZoneColl
                          }
                          isMobileScreen={false}
                          isDownload={isDownload}
                        />
                      </td>
                    </tr>
                  )}

                  {/* Growth Chart Summary */}
                  <tr>
                    <td>
                      <div>
                        {resultData?.memberPhysicalGrowthColl?.length > 0 && (
                          <Typography
                            sx={{
                              padding: "180px 70px 20px 70px",
                              fontWeight: "bold",
                              textAlign: "center",
                            }}
                          >
                            Growth Chart Details
                          </Typography>
                        )}

                        {resultData?.memberPhysicalGrowthColl?.length > 0 &&
                          (motherHeight > 0 ||
                            fatherHeight > 0 ||
                            midParentalHeight > 0) && (
                            <Box
                              sx={{
                                padding: "30px 70px",
                              }}
                            >
                              <TableContainer component={Paper}>
                                <Table size="small">
                                  <TableHead className="table-header-color">
                                    <TableRow>
                                      <TableCell
                                        sx={{
                                          fontWeight: "bold",
                                        }}
                                        align="left"
                                      >
                                        Mother's Height (cm)
                                      </TableCell>
                                      <TableCell
                                        sx={{
                                          display: isDownload
                                            ? "table-cell"
                                            : {
                                                xs: "none",
                                                md: "table-cell",
                                              },
                                          fontWeight: "bold",
                                        }}
                                        align="right"
                                      >
                                        Father's Height (cm)
                                      </TableCell>
                                      <TableCell
                                        sx={{
                                          display: isDownload
                                            ? "table-cell"
                                            : {
                                                xs: "none",
                                                md: "table-cell",
                                              },
                                          fontWeight: "bold",
                                        }}
                                        align="right"
                                      >
                                        Mid Parental Height (cm)
                                      </TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    <TableRow>
                                      <TableCell
                                        sx={{
                                          textAlign: "left",
                                          display: isDownload
                                            ? "table-cell"
                                            : {
                                                xs: "none",
                                                md: "table-cell",
                                              },
                                        }}
                                      >
                                        {motherHeight || ""}
                                      </TableCell>
                                      <TableCell
                                        sx={{
                                          textAlign: "right",
                                          display: isDownload
                                            ? "table-cell"
                                            : {
                                                xs: "none",
                                                md: "table-cell",
                                              },
                                        }}
                                      >
                                        {fatherHeight || ""}
                                      </TableCell>
                                      <TableCell
                                        sx={{
                                          textAlign: "right",
                                          display: isDownload
                                            ? "table-cell"
                                            : {
                                                xs: "none",
                                                md: "table-cell",
                                              },
                                        }}
                                      >
                                        {midParentalHeight || ""}
                                      </TableCell>
                                    </TableRow>
                                  </TableBody>
                                </Table>
                              </TableContainer>
                            </Box>
                          )}

                        {resultData?.memberPhysicalGrowthColl?.length > 0 && (
                          <Box
                            sx={{
                              padding: "30px 70px",
                            }}
                          >
                            <TableContainer component={Paper}>
                              <Table size="small">
                                <TableHead className="table-header-color">
                                  <TableRow>
                                    <TableCell
                                      align="center"
                                      sx={{
                                        fontWeight: "bold",
                                      }}
                                    >
                                      Date
                                    </TableCell>
                                    <TableCell
                                      align="center"
                                      sx={{
                                        fontWeight: "bold",
                                        display: isDownload
                                          ? "table-cell"
                                          : {
                                              xs: "none",
                                              md: "table-cell",
                                            },
                                      }}
                                    >
                                      Age
                                    </TableCell>
                                    <TableCell
                                      align="right"
                                      sx={{
                                        fontWeight: "bold",
                                        display: isDownload
                                          ? "table-cell"
                                          : {
                                              xs: "none",
                                              md: "table-cell",
                                            },
                                      }}
                                    >
                                      Expected Height (cm)
                                    </TableCell>
                                    <TableCell
                                      align="right"
                                      sx={{
                                        fontWeight: "bold",
                                        display: isDownload
                                          ? "table-cell"
                                          : {
                                              xs: "none",
                                              md: "table-cell",
                                            },
                                      }}
                                    >
                                      Weight (kg)
                                    </TableCell>
                                    <TableCell
                                      align="right"
                                      sx={{
                                        fontWeight: "bold",
                                        display: isDownload
                                          ? "table-cell"
                                          : {
                                              xs: "none",
                                              md: "table-cell",
                                            },
                                      }}
                                    >
                                      Height (cm)
                                    </TableCell>
                                    <TableCell
                                      align="center"
                                      sx={{
                                        fontWeight: "bold",
                                        display: isDownload
                                          ? "table-cell"
                                          : {
                                              xs: "none",
                                              md: "table-cell",
                                            },
                                      }}
                                    >
                                      Head Circumference (cm)
                                    </TableCell>
                                    <TableCell
                                      align="right"
                                      sx={{
                                        fontWeight: "bold",
                                        display: isDownload
                                          ? "table-cell"
                                          : {
                                              xs: "none",
                                              md: "table-cell",
                                            },
                                      }}
                                    >
                                      BMI (kg/m²)
                                    </TableCell>
                                    <TableCell
                                      align="center"
                                      sx={{
                                        fontWeight: "bold",
                                        display: isDownload
                                          ? "table-cell"
                                          : {
                                              xs: "none",
                                              md: "table-cell",
                                            },
                                      }}
                                    >
                                      OPD
                                    </TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {resultData?.memberPhysicalGrowthColl.map(
                                    (item, index) => (
                                      <TableRow key={index}>
                                        <TableCell align="center">
                                          {format(
                                            new Date(item.entryDate),
                                            "dd-MM-yyyy",
                                          )}
                                        </TableCell>
                                        <TableCell
                                          align="center"
                                          sx={{
                                            display: isDownload
                                              ? "table-cell"
                                              : {
                                                  xs: "none",
                                                  md: "table-cell",
                                                },
                                          }}
                                        >
                                          {item.familyMemberAge
                                            ? `${item?.familyMemberAge}`
                                            : ""}
                                        </TableCell>
                                        <TableCell
                                          align="right"
                                          sx={{
                                            display: isDownload
                                              ? "table-cell"
                                              : {
                                                  xs: "none",
                                                  md: "table-cell",
                                                },
                                          }}
                                        >
                                          {item?.expectedHeight
                                            ? item?.expectedHeight?.toFixed(2)
                                            : ""}
                                        </TableCell>
                                        <TableCell
                                          align="right"
                                          sx={{
                                            display: isDownload
                                              ? "table-cell"
                                              : {
                                                  xs: "none",
                                                  md: "table-cell",
                                                },
                                          }}
                                        >
                                          {item.weight ? `${item?.weight}` : ""}
                                        </TableCell>
                                        <TableCell
                                          align="right"
                                          sx={{
                                            display: isDownload
                                              ? "table-cell"
                                              : {
                                                  xs: "none",
                                                  md: "table-cell",
                                                },
                                          }}
                                        >
                                          {item.height ? `${item?.height}` : ""}
                                        </TableCell>
                                        <TableCell
                                          align="center"
                                          sx={{
                                            display: isDownload
                                              ? "table-cell"
                                              : {
                                                  xs: "none",
                                                  md: "table-cell",
                                                },
                                          }}
                                        >
                                          {item.headCircumference
                                            ? `${item?.headCircumference}`
                                            : ""}
                                        </TableCell>
                                        <TableCell
                                          align="right"
                                          sx={{
                                            display: isDownload
                                              ? "table-cell"
                                              : {
                                                  xs: "none",
                                                  md: "table-cell",
                                                },
                                          }}
                                        >
                                          {item.bmi ? `${item?.bmi}` : ""}
                                        </TableCell>
                                        <TableCell
                                          align="center"
                                          sx={{
                                            display: isDownload
                                              ? "table-cell"
                                              : {
                                                  xs: "none",
                                                  md: "table-cell",
                                                },
                                          }}
                                        >
                                          {item.caseNumber
                                            ? `${item?.caseNumber}`
                                            : ""}
                                        </TableCell>
                                      </TableRow>
                                    ),
                                  )}
                                </TableBody>
                              </Table>
                            </TableContainer>
                          </Box>
                        )}
                      </div>
                    </td>
                  </tr>

                  {/* Footer */}
                  <tr>
                    <td align="right">
                      <Box
                        sx={{
                          marginTop: "50px",
                          marginRight: "70px",
                          fontWeight: "bold",
                        }}
                      >
                        <span>Run Time: </span>
                        {format(new Date(), "dd-MM-yyyy")}{" "}
                        {format(new Date(), "hh:mm a")}
                      </Box>
                    </td>
                  </tr>
                </table>
              </div>
            );
          })()}
      </Box>
    </>
  );
};

export default GrowthChart;
